import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { FaEye, FaPrint, FaFileExcel, FaFilePdf, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getVendorStatement, clearVendorStatement, clearError, clearSuccess, getVendorsForReport } from '../../../../redux/Slices/AccountingReportsSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from './components/PdfPreviewModal';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

// vemdor statement component
const VendorStatement = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { vendorStatement, vendors, vendorMeta, isLoading, error, success } = useSelector((state) => state.accountingReports);

  const [vendorId, setVendorId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [vendorPage, setVendorPage] = useState(1);
  const vendorSearchInputRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [allMovements, setAllMovements] = useState([]);
  const [localPage, setLocalPage] = useState(1);
  const itemsPerPage = 10;
  const [pdfPages, setPdfPages] = useState([]); // For paginated PDF generation
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const SORT_FIELDS = { DATE: 'date', REFERENCE: 'reference', DESCRIPTION: 'description', DEBIT: 'debit', CREDIT: 'credit', BALANCE: 'balance' };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort style={{ marginLeft: '4px', color: '#ccc', verticalAlign: 'middle' }} />;
    return sortOrder === 'asc'
      ? <FaSortUp style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />
      : <FaSortDown style={{ marginLeft: '4px', color: '#ffd700', verticalAlign: 'middle' }} />;
  };

  useEffect(() => {
    setTitle('accountingReports.vendorStatement', { defaultValue: 'قوائم الموردين' });
    dispatch(getVendorsForReport({ page: 1, per_page: 10 }));
    return () => {
      dispatch(clearVendorStatement());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      toast.success(t(success) || success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, t]);

  useEffect(() => {
    if (error) {
      toast.error(t(error) || error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL, t]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (vendorSearchTerm) {
        dispatch(getVendorsForReport({ search: vendorSearchTerm, page: 1 }));
        setVendorPage(1);
      } else {
        dispatch(getVendorsForReport({ page: vendorPage }));
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [vendorSearchTerm, dispatch]);

  useEffect(() => {
    if (vendorDropdownOpen && !vendorSearchTerm) {
      dispatch(getVendorsForReport({ page: vendorPage }));
    }
  }, [vendorPage, vendorDropdownOpen, vendorSearchTerm, dispatch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (vendorDropdownOpen && !e.target.closest('.vendor-dropdown-container')) {
        setVendorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [vendorDropdownOpen]);

  const filteredVendors = useMemo(() => {
    if (!Array.isArray(vendors)) return [];
    if (!vendorSearchTerm) return vendors;
    const term = vendorSearchTerm.toLowerCase();
    return vendors.filter((vendor) => {
      const nameAr = vendor.name_ar?.toLowerCase() || '';
      const nameEn = vendor.name_en?.toLowerCase() || '';
      const code = vendor.code?.toLowerCase() || '';
      return nameAr.includes(term) || nameEn.includes(term) || code.includes(term);
    });
  }, [vendors, vendorSearchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!vendorId) {
      toast.error(t('accountingReports.selectVendor', { defaultValue: 'برجاء اختيار المورد أولاً' }), { rtl: isRTL });
      return;
    }
    // First fetch to get total
    const result = await dispatch(
      getVendorStatement({
        vendor_id: vendorId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: 1,
      })
    );
    // Then fetch all with per_page = total
    if (result.payload?.data?.movements?.total) {
      const total = result.payload.data.movements.total;
      if (total > 0) {
        await dispatch(
          getVendorStatement({
            vendor_id: vendorId,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            per_page: total,
          })
        );
      }
    }
    setLocalPage(1);
  };

  const preparePdfPages = () => {
    const topHeader = document.getElementById('vendor-statement-pdf-measure-top-header');
    const thead = document.getElementById('vendor-statement-pdf-measure-thead');
    const footer = document.getElementById('vendor-statement-pdf-measure-footer');
    const tbody = document.getElementById('vendor-statement-pdf-measure-tbody');
    const chunks = calculatePdfPagination(allMovements, { topHeader, thead, footer, tbody });
    return chunks.map((chunk, idx) => ({
      pageNumber: idx + 1,
      movements: chunk.items || chunk,
      startIndex: chunk.startIndex ?? idx * 7,
      showTotals: idx === chunks.length - 1,
      totals: idx === chunks.length - 1 ? totals : null,
      showOpeningBalance: idx === 0,
      showSummaryCards: idx === 0,
    }));
  };

  const handlePreviewAll = () => {
    if (!vendorStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    // Prepare PDF pages for preview
    const pages = preparePdfPages();
    setPdfPages(pages);
    setIsPreviewOpen(true);
  };

  const handlePrintAll = async () => {
    if (!vendorStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setPdfType('all');
      
      // Prepare PDF pages with chunks
      const pages = preparePdfPages();
      setPdfPages(pages);
      
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const vendorName = selectedVendor
        ? (isRTL
            ? selectedVendor.name_ar || selectedVendor.name_en || ''
            : selectedVendor.name_en || selectedVendor.name_ar || '')
        : '';
      const pageElementIds = pages.map((_, i) =>
        document.getElementById(`vendor-statement-pdf-all-${i}`)
          ? `vendor-statement-pdf-all-${i}`
          : `preview-vendor-statement-pdf-all-${i}`,
      );
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_VendorStatement_${vendorName || vendorId}_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('accountingReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('accountingReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfPages([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!vendorStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const data = vendorStatement?.data || vendorStatement || {};
      const totalsForExcel = data?.totals || {};
      const movementsForExcel = Array.isArray(data?.movements?.data)
        ? data.movements.data
        : Array.isArray(data?.movements)
          ? data.movements
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.entries)
              ? data.entries
              : [];

      const excelData = [
        {
          [t('accountingReports.vendor', { defaultValue: 'المورد' })]: selectedVendor ? (isRTL ? (selectedVendor.name_ar || selectedVendor.name_en || '') : (selectedVendor.name_en || selectedVendor.name_ar || '')) : vendorId,
          [t('accountingReports.period', { defaultValue: 'الفترة' })]: `${formatDate(startDate)} - ${formatDate(endDate)}`,
        },
        {},
        {
          [t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })]: formatNumber(totalsForExcel.opening_balance || 0),
          [t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })]: formatNumber(totalsForExcel.total_debit || 0),
          [t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })]: formatNumber(totalsForExcel.total_credit || 0),
          [t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })]: formatNumber(totalsForExcel.closing_balance || 0),
        },
        {},
        {
          [t('accountingReports.date', { defaultValue: 'التاريخ' })]: t('accountingReports.date', { defaultValue: 'التاريخ' }),
          [t('accountingReports.reference', { defaultValue: 'المرجع' })]: t('accountingReports.reference', { defaultValue: 'المرجع' }),
          [t('accountingReports.description', { defaultValue: 'الوصف' })]: t('accountingReports.description', { defaultValue: 'الوصف' }),
          [t('accountingReports.debit', { defaultValue: 'مدين' })]: t('accountingReports.debit', { defaultValue: 'مدين' }),
          [t('accountingReports.credit', { defaultValue: 'دائن' })]: t('accountingReports.credit', { defaultValue: 'دائن' }),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: t('accountingReports.balance', { defaultValue: 'الرصيد' }),
        },
        ...movementsForExcel.map((movement) => ({
          [t('accountingReports.date', { defaultValue: 'التاريخ' })]: formatDate(movement.date || movement.created_at),
          [t('accountingReports.reference', { defaultValue: 'المرجع' })]: movement.reference || movement.code || movement.document_number || '—',
          [t('accountingReports.description', { defaultValue: 'الوصف' })]: movement.description || movement.note || movement.memo || '—',
          [t('accountingReports.debit', { defaultValue: 'مدين' })]: formatNumber(movement.debit || 0),
          [t('accountingReports.credit', { defaultValue: 'دائن' })]: formatNumber(movement.credit || 0),
          [t('accountingReports.balance', { defaultValue: 'الرصيد' })]: formatNumber(movement.balance || movement.running_balance || 0),
        })),
        {},
        {
          [t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })]: formatNumber(totalsForExcel.total_debit || 0),
          [t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })]: formatNumber(totalsForExcel.total_credit || 0),
          [t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })]: formatNumber(totalsForExcel.closing_balance || 0),
        },
      ];

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendor Statement');
      const vendorName = selectedVendor ? (isRTL ? (selectedVendor.name_ar || selectedVendor.name_en || '') : (selectedVendor.name_en || selectedVendor.name_ar || '')) : vendorId;
      XLSX.writeFile(workbook, `VendorStatement_${vendorName || vendorId}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('accountingReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    );
  };

  const reportData = vendorStatement?.data || vendorStatement || {};
  const totals = reportData?.totals || {};
  const selectedVendor = vendors.find(v => String(v.id) === String(vendorId));

  // Update allMovements when vendorStatement changes
  useEffect(() => {
    const movements = reportData?.movements?.data || [];
    if (movements.length > 0) {
      setAllMovements(movements);
      setLocalPage(1);
    } else {
      setAllMovements([]);
    }
  }, [vendorStatement, reportData?.movements?.data]);

  // Sorting
  const sortedMovements = useMemo(() => {
    if (!sortField) return allMovements;
    return [...allMovements].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortOrder === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [allMovements, sortField, sortOrder]);

  // Local pagination
  const totalPages = Math.ceil(sortedMovements.length / itemsPerPage);
  const paginatedMovements = useMemo(() => {
    const startIndex = (localPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedMovements.slice(startIndex, endIndex);
  }, [sortedMovements, localPage]);

  const handleLocalPageChange = (page) => {
    setLocalPage(page);
  };

  const movementsData = paginatedMovements.length > 0 ? paginatedMovements : [];

  return (
    <div style={{ padding: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ 
        backgroundColor: '#1b3350', 
        borderRadius: '12px', 
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h2 style={{ color: 'white', marginBottom: '20px', direction: dir, textAlign }}>
          {t('accountingReports.vendorStatement', { defaultValue: 'قوائم الموردين' })}
        </h2>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{ 
          backgroundColor: '#1a1f2e', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div style={{ position: 'relative' }} className="vendor-dropdown-container">
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.vendor', { defaultValue: 'المورد' })} <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <div
                onClick={() => {
                  setVendorDropdownOpen(!vendorDropdownOpen);
                  if (!vendorDropdownOpen) {
                    setTimeout(() => {
                      if (vendorSearchInputRef.current) {
                        vendorSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>
                  {selectedVendor 
                    ? (isRTL ? (selectedVendor.name_ar || selectedVendor.name_en || `#${vendorId}`) : (selectedVendor.name_en || selectedVendor.name_ar || `#${vendorId}`))
                    : t('accountingReports.selectVendor', { defaultValue: 'اختر المورد' })}
                </span>
                <span>▼</span>
              </div>
              {vendorDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}>
                  <div style={{ padding: '8px', borderBottom: '1px solid #333', position: 'sticky', top: 0, backgroundColor: '#14263b', zIndex: 2 }}>
                    <input
                      ref={vendorSearchInputRef}
                      type="text"
                      placeholder={t('accountingReports.searchVendor', { defaultValue: 'بحث عن مورد...' })}
                      value={vendorSearchTerm}
                      onChange={(e) => setVendorSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: '#0d1117',
                        border: 'none',
                        borderBottom: '1px solid #333',
                        color: 'white',
                        borderRadius: '6px 6px 0 0'
                      }}
                    />
                  </div>
                  {vendorMeta && vendorMeta.last_page >= 1 && (
                    <div style={{
                      padding: '10px',
                      borderBottom: '1px solid #333',
                      position: 'sticky',
                      top: 52,
                      backgroundColor: '#14263b',
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}>
                      <div style={{ color: '#ccc', fontSize: '12px', textAlign: 'center', direction: dir }}>
                        {t('accountingReports.page', { defaultValue: 'صفحة' })} {vendorMeta.current_page || vendorPage} {t('accountingReports.of', { defaultValue: 'من' })} {vendorMeta.last_page || 1}
                    </div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if ((vendorMeta.current_page || vendorPage) > 1) {
                              setVendorPage(1);
                            }
                          }}
                          disabled={(vendorMeta.current_page || vendorPage) === 1}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: (vendorMeta.current_page || vendorPage) === 1 ? '#2a2a2a' : '#1a1f2e',
                            color: (vendorMeta.current_page || vendorPage) === 1 ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: (vendorMeta.current_page || vendorPage) === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('accountingReports.first', { defaultValue: 'الأولى' })}
                        >
                          ««
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if ((vendorMeta.current_page || vendorPage) > 1) {
                              setVendorPage((prev) => Math.max(1, prev - 1));
                            }
                          }}
                          disabled={(vendorMeta.current_page || vendorPage) === 1}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: (vendorMeta.current_page || vendorPage) === 1 ? '#2a2a2a' : '#1a1f2e',
                            color: (vendorMeta.current_page || vendorPage) === 1 ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: (vendorMeta.current_page || vendorPage) === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('accountingReports.previous', { defaultValue: 'السابق' })}
                        >
                          ‹
                        </button>
                        {(() => {
                          const pages = [];
                          const current = vendorMeta.current_page || vendorPage;
                          const last = vendorMeta.last_page || 1;
                          const showPages = 5;
                          let start = Math.max(1, current - Math.floor(showPages / 2));
                          let end = Math.min(last, start + showPages - 1);
                          if (end - start < showPages - 1) {
                            start = Math.max(1, end - showPages + 1);
                          }
                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }
                          return pages.map((page) => (
                        <button
                              key={page}
                              type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                                setVendorPage(page);
                          }}
                          style={{
                                padding: '4px 8px',
                                backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                            color: 'white',
                                border: page === current ? '1px solid #AC2000' : '1px solid #444',
                            borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 10,
                                fontWeight: page === current ? 'bold' : 'normal',
                                minWidth: '28px',
                              }}
                            >
                              {page}
                            </button>
                          ));
                        })()}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if ((vendorMeta.current_page || vendorPage) < (vendorMeta.last_page || 1)) {
                              setVendorPage((prev) => prev + 1);
                            }
                          }}
                          disabled={(vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: (vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1) ? '#2a2a2a' : '#1a1f2e',
                            color: (vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1) ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: (vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1) ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('accountingReports.next', { defaultValue: 'التالي' })}
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if ((vendorMeta.current_page || vendorPage) < (vendorMeta.last_page || 1)) {
                              setVendorPage(vendorMeta.last_page || 1);
                            }
                          }}
                          disabled={(vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: (vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1) ? '#2a2a2a' : '#1a1f2e',
                            color: (vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1) ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: (vendorMeta.current_page || vendorPage) === (vendorMeta.last_page || 1) ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('accountingReports.last', { defaultValue: 'الأخيرة' })}
                        >
                          »»
                        </button>
                      </div>
                    </div>
                  )}
                  {filteredVendors.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#ccc' }}>
                      {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                    </div>
                  ) : (
                    <>
                      {filteredVendors.map((vendor) => (
                        <div
                          key={vendor.id}
                          onClick={() => {
                            setVendorId(String(vendor.id));
                            setVendorDropdownOpen(false);
                            setVendorSearchTerm('');
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #333',
                            color: String(vendorId) === String(vendor.id) ? '#AC2000' : 'white',
                            backgroundColor: String(vendorId) === String(vendor.id) ? '#2a2a2a' : 'transparent'
                          }}
                        >
                          {vendor.code ? `${vendor.code} - ` : ''}{isRTL ? (vendor.name_ar || vendor.name_en || `#${vendor.id}`) : (vendor.name_en || vendor.name_ar || `#${vendor.id}`)}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.startDate', { defaultValue: 'تاريخ البداية' })}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.endDate', { defaultValue: 'تاريخ النهاية' })}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                opacity: isLoading ? 0.6 : 1,
                minWidth: '180px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isLoading ? t('accountingReports.loading', { defaultValue: 'جاري التحميل...' }) : t('accountingReports.view', { defaultValue: 'عرض' })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {vendorStatement && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {vendorStatement && (
          <div style={{ 
            backgroundColor: '#1a1f2e', 
            padding: '20px', 
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ccc', marginBottom: '20px', direction: dir, textAlign }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('accountingReports.vendor', { defaultValue: 'المورد' })}:</strong> {selectedVendor ? (isRTL ? (selectedVendor.name_ar || selectedVendor.name_en || '') : (selectedVendor.name_en || selectedVendor.name_ar || '')) : vendorId}
            </div>
              <div>
                <strong>{t('accountingReports.period', { defaultValue: 'الفترة' })}:</strong> {formatDate(startDate)} - {formatDate(endDate)}
              </div>
            </div>

            {/* Summary Cards */}
            {totals && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })}
                  </div>
                  <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(totals.opening_balance || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })}
                  </div>
                  <div style={{ color: '#dc3545', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(totals.total_debit || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })}
                  </div>
                  <div style={{ color: '#28a745', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(totals.total_credit || 0)}
                  </div>
                </div>
                <div style={{ padding: '15px', backgroundColor: '#1b3350', borderRadius: '6px', border: '1px solid #333' }}>
                  <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '5px', direction: dir, textAlign }}>
                    {t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })}
                  </div>
                  <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', direction: dir, textAlign }}>
                    {formatNumber(totals.closing_balance || 0)}
                  </div>
                </div>
              </div>
            )}

            {/* Movements Table */}
            <div>
              <h3 style={{ color: 'white', marginBottom: '15px', fontSize: '18px', direction: dir, textAlign }}>
                {t('accountingReports.movements', { defaultValue: 'الحركات' })}
              </h3>
              <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#1b3350' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#AC2000' }}>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', width: '36px' }}>#</th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.DATE)}>
                        {t('accountingReports.date', { defaultValue: 'التاريخ' })}{getSortIcon(SORT_FIELDS.DATE)}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.REFERENCE)}>
                        {t('accountingReports.reference', { defaultValue: 'المرجع' })}{getSortIcon(SORT_FIELDS.REFERENCE)}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.DESCRIPTION)}>
                        {t('accountingReports.description', { defaultValue: 'الوصف' })}{getSortIcon(SORT_FIELDS.DESCRIPTION)}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.DEBIT)}>
                        {t('accountingReports.debit', { defaultValue: 'مدين' })}{getSortIcon(SORT_FIELDS.DEBIT)}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.CREDIT)}>
                        {t('accountingReports.credit', { defaultValue: 'دائن' })}{getSortIcon(SORT_FIELDS.CREDIT)}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', cursor: 'pointer' }} onClick={() => handleSort(SORT_FIELDS.BALANCE)}>
                        {t('accountingReports.balance', { defaultValue: 'الرصيد' })}{getSortIcon(SORT_FIELDS.BALANCE)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {allMovements.length > 0 ? (
                      movementsData.map((movement, idx) => (
                        <tr key={movement.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e' }}>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {(localPage - 1) * itemsPerPage + idx + 1}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {formatDate(movement.date || movement.created_at)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {movement.reference || movement.code || movement.document_number || '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333', maxWidth: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {movement.description || movement.note || movement.memo || '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545', border: '1px solid #333' }}>
                            {formatNumber(movement.debit || 0)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: '#28a745', border: '1px solid #333' }}>
                            {formatNumber(movement.credit || 0)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {formatNumber(movement.balance || movement.running_balance || 0)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ padding: '14px', textAlign: 'center', color: '#ccc', border: '1px solid #333' }}>
                          {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {totals && (
                    <tfoot>
                      <tr style={{ backgroundColor: '#111827' }}>
                        <td colSpan="4" style={{ padding: '12px', textAlign: 'center', color: '#ccc', border: '1px solid #333' }}>
                          {t('accountingReports.totals', { defaultValue: 'الإجماليات' })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#dc3545', border: '1px solid #333' }}>
                          {formatNumber(totals.total_debit || 0)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: '#28a745', border: '1px solid #333' }}>
                          {formatNumber(totals.total_credit || 0)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                          {formatNumber(totals.closing_balance || 0)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
                {vendorId && allMovements.length > 0 && totalPages > 1 && (
                  <div
                    style={{
                      padding: '18px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      borderTop: '1px solid #333',
                    }}
                  >
                    <button
                      onClick={() => handleLocalPageChange(Math.max(1, localPage - 1))}
                      disabled={localPage === 1}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: localPage > 1 ? 'pointer' : 'not-allowed',
                        opacity: localPage > 1 ? 1 : 0.5,
                        fontSize: '14px',
                      }}
                    >
                      &lt;
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={`page-${page}`}
                        onClick={() => handleLocalPageChange(page)}
                        style={{
                          backgroundColor:
                            page === localPage ? '#AC2000' : '#1b3350',
                          color: 'white',
                          border:
                            page === localPage ? 'none' : '1px solid #333',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: page === localPage ? 'bold' : 'normal',
                        }}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handleLocalPageChange(Math.min(totalPages, localPage + 1))}
                      disabled={localPage === totalPages}
                      style={{
                        backgroundColor: '#1b3350',
                        color: 'white',
                        border: '1px solid #333',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        cursor: localPage < totalPages ? 'pointer' : 'not-allowed',
                        opacity: localPage < totalPages ? 1 : 0.5,
                        fontSize: '14px',
                      }}
                    >
                      &gt;
                    </button>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>

      {/* PDF Print Templates */}
      {pdfType === 'all' && vendorStatement && pdfPages && pdfPages.length > 0 && (
        pdfPages.map((page, pageIndex) => (
        <div
          key={pageIndex}
          id={`vendor-statement-pdf-all-${pageIndex}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            minHeight: '1123px',
            height: 'auto',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
            overflow: 'visible'
          }}
        >
          <div style={{ 
            border: '5px solid #000',
            padding: '3px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            minHeight: '1043px',
            height: 'auto',
            overflow: 'visible'
          }}>
            <div style={{ 
              border: '2px solid #000', 
              minHeight: '1033px',
              height: '1033px',
              padding: '18px 18px 10px', 
              position: 'relative', 
              display: 'flex', 
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              textRendering: 'optimizeLegibility',
              direction: isRTL ? 'rtl' : 'ltr',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              overflow: 'visible'
            }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                height: '600px',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                <img 
                  src={`${publicUrl}/HeaderAce.png`} 
                  alt="Header" 
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }} 
                  crossOrigin="anonymous" 
                />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                      {t('accountingReports.vendorStatementTitle', { defaultValue: 'قوائم الموردين' })}
                    </h2>
                    <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                      {selectedVendor 
                        ? (isRTL ? (selectedVendor.name_ar || selectedVendor.name_en || '') : (selectedVendor.name_en || selectedVendor.name_ar || ''))
                        : vendorId}
                    </div>
                    <div style={{ fontSize: '14px', marginTop: '4px', direction: dir }}>
                      {formatDate(startDate)} - {formatDate(endDate)}
                    </div>
                  </div>

                    {pageIndex === 0 && totals && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '18px' }}>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', direction: dir, textAlign }}>{formatNumber(totals.opening_balance || 0)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#b22222', direction: dir, textAlign }}>{formatNumber(totals.total_debit || 0)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f5132', direction: dir, textAlign }}>{formatNumber(totals.total_credit || 0)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', direction: dir, textAlign }}>{formatNumber(totals.closing_balance || 0)}</div>
                        </div>
                      </div>
                    )}

                    <div style={{ border: '1px solid #000', borderRadius: '6px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', width: '30px' }}>#</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.date', { defaultValue: 'التاريخ' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.reference', { defaultValue: 'المرجع' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.description', { defaultValue: 'الوصف' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.debit', { defaultValue: 'مدين' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.credit', { defaultValue: 'دائن' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {page.movements && page.movements.length > 0 ? (
                            page.movements.map((movement, idx) => (
                              <tr key={movement.id || idx}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{(page.startIndex ?? 0) + idx + 1}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatDate(movement.date || movement.created_at)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{movement.reference || movement.code || movement.document_number || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir, maxWidth: '220px', whiteSpace: 'nowrap' }}>
                                  {movement.description || movement.note || movement.memo || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(movement.debit || 0)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(movement.credit || 0)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(movement.balance || movement.running_balance || 0)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" style={{ border: '1px solid #000', padding: '10px', textAlign: 'center' }}>
                                {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {page.showTotals && page.totals && (
                          <tfoot>
                            <tr>
                              <td colSpan="4" style={{ border: '1px solid #000', padding: '8px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                {t('accountingReports.totals', { defaultValue: 'الإجماليات' })}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                {formatNumber(page.totals.total_debit || 0)}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                {formatNumber(page.totals.total_credit || 0)}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                {formatNumber(page.totals.closing_balance || 0)}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                </div>

                <div style={{ 
                  position: 'absolute',
                  bottom: '10px',
                  left: '18px',
                  right: '18px',
                  flexShrink: 0
                }}>
                  <img 
                    src={`${publicUrl}/FooterAce.png`} 
                    alt="Footer" 
                    style={{ 
                      width: '100%', 
                      height: 'auto', 
                      objectFit: 'contain',
                      borderTop: '4px double #000',
                      paddingTop: '8px',
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        ))
      )}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && !!vendorStatement}
        title={t('accountingReports.vendorStatementTitle', { defaultValue: 'قوائم الموردين' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
            >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            {pdfPages && pdfPages.length > 0 && pdfPages.map((page, pageIndex) => (
              <div
                key={pageIndex}
                id={`preview-vendor-statement-pdf-all-${pageIndex}`}
                style={{
                  width: '794px',
                  minHeight: '1123px',
                  height: 'auto',
                  backgroundColor: '#ffffff',
                  color: '#000000',
                  fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: 'bold',
                  padding: '40px',
                  boxSizing: 'border-box',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                }}
              >
            <div style={{ 
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
              height: 'auto',
              overflow: 'visible'
            }}>
              <div style={{ 
                border: '2px solid #000', 
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px', 
                position: 'relative', 
                display: 'flex', 
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                direction: isRTL ? 'rtl' : 'ltr',
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                height: '600px',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img 
                    src={`${publicUrl}/HeaderAce.png`} 
                    alt="Header" 
                    style={{ 
                      width: '100%', 
                      height: 'auto', 
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0
                    }} 
                    crossOrigin="anonymous" 
                  />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                        {t('accountingReports.vendorStatementTitle', { defaultValue: 'قوائم الموردين' })}
                      </h2>
                      <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                        {selectedVendor 
                        ? (isRTL ? (selectedVendor.name_ar || selectedVendor.name_en || '') : (selectedVendor.name_en || selectedVendor.name_ar || ''))
                        : vendorId}
                      </div>
                      <div style={{ fontSize: '14px', marginTop: '4px', direction: dir }}>
                        {formatDate(startDate)} - {formatDate(endDate)}
                      </div>
                    </div>

                    {pageIndex === 0 && totals && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '18px' }}>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.openingBalance', { defaultValue: 'الرصيد الافتتاحي' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', direction: dir, textAlign }}>{formatNumber(totals.opening_balance || 0)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.totalDebit', { defaultValue: 'إجمالي مدين' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#b22222', direction: dir, textAlign }}>{formatNumber(totals.total_debit || 0)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.totalCredit', { defaultValue: 'إجمالي دائن' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f5132', direction: dir, textAlign }}>{formatNumber(totals.total_credit || 0)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5' }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px', direction: dir, textAlign }}>{t('accountingReports.closingBalance', { defaultValue: 'الرصيد الختامي' })}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', direction: dir, textAlign }}>{formatNumber(totals.closing_balance || 0)}</div>
                        </div>
                      </div>
                    )}

                    <div style={{ border: '1px solid #000', borderRadius: '6px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr', fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', width: '30px' }}>#</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.date', { defaultValue: 'التاريخ' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.reference', { defaultValue: 'المرجع' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.description', { defaultValue: 'الوصف' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.debit', { defaultValue: 'مدين' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.credit', { defaultValue: 'دائن' })}</th>
                            <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {page.movements && page.movements.length > 0 ? (
                            page.movements.map((movement, idx) => (
                              <tr key={movement.id || idx}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{(page.startIndex ?? 0) + idx + 1}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatDate(movement.date || movement.created_at)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{movement.reference || movement.code || movement.document_number || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir, maxWidth: '220px', whiteSpace: 'nowrap' }}>
                                  {movement.description || movement.note || movement.memo || '—'}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(movement.debit || 0)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(movement.credit || 0)}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{formatNumber(movement.balance || movement.running_balance || 0)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" style={{ border: '1px solid #000', padding: '10px', textAlign: 'center' }}>
                                {t('accountingReports.noData', { defaultValue: 'لا توجد بيانات' })}
                              </td>
                            </tr>
                          )}
                        </tbody>
                        {page.showTotals && page.totals && (
                          <tfoot>
                            <tr>
                              <td colSpan="4" style={{ border: '1px solid #000', padding: '8px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                {t('accountingReports.totals', { defaultValue: 'الإجماليات' })}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                {formatNumber(page.totals.total_debit || 0)}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                {formatNumber(page.totals.total_credit || 0)}
                              </td>
                              <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                                {formatNumber(page.totals.closing_balance || 0)}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                  <div style={{ 
                    position: 'absolute',
                    bottom: '10px',
                    left: '18px',
                    right: '18px',
                    flexShrink: 0
                  }}>
                    <img 
                      src={`${publicUrl}/FooterAce.png`} 
                      alt="Footer" 
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderTop: '4px double #000',
                        paddingTop: '8px',
                      }} 
                      crossOrigin="anonymous" 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
            ))}
          </div>
      </PdfPreviewModal>

      {/* Measurement template for dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="vendor-statement-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <h2 style={{ textAlign: 'center', fontSize: '18px', margin: '8px 0' }}>{t('accountingReports.vendorStatementTitle', { defaultValue: 'كشف حساب المورد' })}</h2>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000' }}>
          <thead id="vendor-statement-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('accountingReports.date', { defaultValue: 'التاريخ' })}</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('accountingReports.reference', { defaultValue: 'المرجع' })}</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('accountingReports.description', { defaultValue: 'الوصف' })}</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('accountingReports.debit', { defaultValue: 'مدين' })}</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('accountingReports.credit', { defaultValue: 'دائن' })}</th>
              <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('accountingReports.balance', { defaultValue: 'الرصيد' })}</th>
            </tr>
          </thead>
          <tbody id="vendor-statement-pdf-measure-tbody">
            {allMovements.slice(0, 5).map((movement, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{movement.date || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{movement.reference || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{movement.description || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{movement.debit || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{movement.credit || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{movement.balance || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="vendor-statement-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

export default VendorStatement;
